import { throttle } from 'throttle-debounce';
import { SETTINGS, BREAKPOINTS, CLASSES, ARIAS, SELECTORS } from '../../utils/constants';
import initSearch from './search';

const initHeader = () => {
  const headerEl = document.querySelector('[data-header]');
  if (!headerEl) return;
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP_LARGE}px)`);
  const announcementBarEl = document.querySelector('[data-announcement-bar]');
  const navToggle = headerEl.querySelector('[data-nav-toggle]');
  const navToggleText = navToggle?.querySelector('[data-nav-toggle-text]');
  const mobileSearchToggle = headerEl.querySelector('[data-mobile-search-toggle]');
  const quickLinksEl = headerEl.querySelector('[data-header-quick-links]');
  const hamburgerToggle = headerEl.querySelector(SELECTORS.HAMBURGER_TOGGLE);
  const navigationEl = headerEl.querySelector('[data-navigation]');
  const navigationWrapperEl = navigationEl.querySelector('[data-navigation-wrapper]');
  const navMenuEls = navigationEl.querySelectorAll('[data-navigation-menu]');

  const mainEl = document.querySelector('main');
  const isHomepage = mainEl.classList.contains('template-index');

  let lastScrollY = window.scrollY;
  let isScrollHandlerAttached = false;
  let closeHamburgerMenu = null;
  let transparentHeaderUpdate = null;

  const getStickyStatus = () => {
    if (!announcementBarEl) return;
    const announcementBarContainer = announcementBarEl.parentElement;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          headerEl.removeAttribute('data-sticky-active');
        } else {
          headerEl.setAttribute('data-sticky-active', 'true');
        }
      },
      { threshold: [0] }
    );
    if (announcementBarContainer) observer.observe(announcementBarContainer);
  };


  const hideShowStickyEls = () => {
    if (!quickLinksEl) return;
    const currentScrollY = window.scrollY;
    const deltaY = currentScrollY - lastScrollY;
    if (deltaY === 0) return;
    if (deltaY > 0) {
      quickLinksEl.classList.add('hidden');
      if (mobileSearchToggle?.getAttribute(ARIAS.EXPANDED) === 'true') {
        window.theme?.search?.closeDrawer?.();
      }
    } else {
      if (isHomepage) {
        quickLinksEl.classList.remove('hidden');
      }
    }
    lastScrollY = currentScrollY;
  };

  const throttledHideShowStickyEls = quickLinksEl ? throttle(SETTINGS.THROTTLE_DELAY_SMALL, hideShowStickyEls) : null;
  const setStickyScrollListener = (breakpoint) => {
    if (throttledHideShowStickyEls === null) return;
    if (breakpoint.matches) {
      if (isScrollHandlerAttached) {
        window.removeEventListener('scroll', throttledHideShowStickyEls);
        isScrollHandlerAttached = false;
      }
    } else {
      if (!isScrollHandlerAttached) {
        window.addEventListener('scroll', throttledHideShowStickyEls);
        isScrollHandlerAttached = true;
      }
    }
  };


  const closeSubNav = (subNav, level, closeBtn, handler) => {
    subNav.classList.remove(CLASSES.TRANSLATE_X_0);
    if (level === '2') {
      navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
    } else {
      const parentMenu = subNav.closest(SELECTORS.NAVIGATION_MENU_2);
      subNav.classList.remove(CLASSES.SCROLLABLE);
      if (parentMenu) {
        parentMenu.classList.remove(CLASSES.OVERFLOW_HIDDEN);
      }
    }
    closeBtn.removeEventListener('click', handler);
  };

  const openSubNav = (subNav, level) => {
    if (!subNav) return;
    subNav.classList.add(CLASSES.TRANSLATE_X_0);
    navigationWrapperEl.classList.add(CLASSES.OVERFLOW_HIDDEN);
    if (level === '3') {
      const parentMenu = subNav.closest(SELECTORS.NAVIGATION_MENU_2);
      if (parentMenu) {
        parentMenu.scrollTop = 0;
        subNav.classList.add(CLASSES.SCROLLABLE);
        parentMenu.classList.add(CLASSES.OVERFLOW_HIDDEN);
      }
    }

    const closeBtn = subNav.querySelector(SELECTORS.NAVIGATION_CLOSE);
    if (closeBtn) {
      const handleClose = () => closeSubNav(subNav, level, closeBtn, handleClose);
      closeBtn.addEventListener('click', handleClose);
    }
  };

  const bindNavigationLinks = (links) => {
    links.forEach(link => {
      link.addEventListener('click', (evt) => {
        const targetAttr = link.getAttribute('target');
        if (targetAttr === '_blank') {

          evt.stopImmediatePropagation();
          return;
        }

        if (desktopMediaQuery.matches) return;

        evt.preventDefault();

        const subNav = link.nextElementSibling;
        if (!subNav || !subNav.dataset.navigationMenu) return;

        openSubNav(subNav, subNav.dataset.navigationMenu);
      }, { capture: true });
    });
  };

  const resetNavigation = () => {
    navMenuEls.forEach(menu => {
      menu.classList.remove(CLASSES.TRANSLATE_X_0);
      menu.scrollTop = 0;
    });
    navigationEl.scrollTop = 0;
    navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
  };

  if (navToggle) {
    navToggle.addEventListener('click', () => {
      const isOpen = navToggle.classList.toggle('nav-open');
      navToggle.setAttribute(ARIAS.EXPANDED, isOpen);
      if (navToggleText) {
        navToggleText.textContent = isOpen ? navToggleText.dataset.navToggleCloseText : navToggleText.dataset.navToggleText;
      }
      navigationEl.classList.toggle(CLASSES.TRANSLATE_X_0);
      isOpen ? resetNavigation() : null;
    });
  }

  // Bind ALL navigation links (not just ones with data attributes)
  const allNavLinks = navigationEl.querySelectorAll('a.navigation-item');
  bindNavigationLinks(allNavLinks);

  const updateMainOffset = () => {
    const isFixed = headerEl.hasAttribute('data-header-fixed');
    if (!isFixed) {
      mainEl?.style.removeProperty('padding-top');
      return;
    }

    const announcementHeight = announcementBarEl?.offsetHeight || 0;
    const totalHeight = announcementHeight + headerEl.offsetHeight;
    const isTransparent = headerEl.classList.contains(CLASSES.TRANSPARENT_HEADER);
    const firstSection = mainEl?.querySelector(':scope > .shopify-section');
    const firstSectionHasHero = !!firstSection?.querySelector('[data-hero]');

    if (isTransparent && firstSectionHasHero) {
      mainEl?.style.removeProperty('padding-top');
      return;
    }

    if (mainEl) mainEl.style.paddingTop = `${totalHeight}px`;
  };

  const initTransparentHeader = () => {
    if (!headerEl.classList.contains(CLASSES.TRANSPARENT_HEADER)) return;
    if (mainEl.classList.contains('template-article') || mainEl.classList.contains('template-page')) {
      headerEl.classList.remove(CLASSES.TRANSPARENT_HEADER);
      headerEl.classList.add(CLASSES.HEADER_OPAQUE);
      updateMainOffset();
      return;
    }

    const setOpaque = () => {
      headerEl.classList.remove(CLASSES.TRANSPARENT_HEADER);
      headerEl.classList.add(CLASSES.HEADER_OPAQUE);
    };

    const setTransparent = () => {
      headerEl.classList.remove(CLASSES.HEADER_OPAQUE);
      headerEl.classList.add(CLASSES.TRANSPARENT_HEADER);
    };

    const updateBg = () => {
      const isScrolled = window.scrollY > 0;
      const hamburgerOpen = hamburgerToggle?.getAttribute(ARIAS.EXPANDED) === 'true';
      const navItemOpen = Array.from(navigationEl.querySelectorAll('[data-nav-desktop-item]')).some(
        el => el.classList.contains('nav-open')
      );
      if (isScrolled || hamburgerOpen || navItemOpen) {
        setOpaque();
      } else {
        setTransparent();
      }
    };

    transparentHeaderUpdate = updateBg;

    window.addEventListener('scroll', throttle(SETTINGS.THROTTLE_DELAY_SMALL, updateBg), { passive: true });
    headerEl.addEventListener('mouseenter', setOpaque);
    headerEl.addEventListener('mouseleave', updateBg);

    updateBg();
  };

  const initHamburgerMenu = () => {
    const toggle = headerEl.querySelector(SELECTORS.HAMBURGER_TOGGLE);
    const menu = document.querySelector('[data-hamburger-megamenu]');
    const overlay = document.querySelector('[data-hamburger-overlay]');

    if (!toggle || !menu) return;

    const getHeaderBottom = () => headerEl.getBoundingClientRect().bottom + 'px';

    // Subnav slide-outs within the hamburger menu
    const hamburgerNavParents = menu.querySelectorAll('[data-navigation-parent]');
    const hamburgerSubnavs = menu.querySelectorAll('[data-hamburger-subnav]');
    const grandchildMenus = menu.querySelectorAll('[data-navigation-menu="3"]');

    const closeAllSubnavs = () => {
      [...hamburgerSubnavs, ...grandchildMenus].forEach(el => el.classList.remove(CLASSES.TRANSLATE_X_0));
    };

    hamburgerNavParents.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const subnav = link.nextElementSibling;
        if (!subnav || !subnav.hasAttribute('data-hamburger-subnav')) return;
        const topValue = menu.style.top || getHeaderBottom();
        subnav.style.top = topValue;
        subnav.classList.add(CLASSES.TRANSLATE_X_0);
      });
    });

    [...hamburgerSubnavs, ...grandchildMenus].forEach(el => {
      const backBtn = el.querySelector(SELECTORS.NAVIGATION_CLOSE);
      if (backBtn) {
        backBtn.addEventListener('click', () => el.classList.remove(CLASSES.TRANSLATE_X_0));
      }
    });

    // Handle level 3 grandchild navigation within hamburger subnavs
    const hamburgerChildLinks = menu.querySelectorAll('[data-navigation-child]');
    hamburgerChildLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const grandchildMenu = link.nextElementSibling;
        if (!grandchildMenu || grandchildMenu.dataset.navigationMenu !== '3') return;
        grandchildMenu.style.top = menu.style.top || getHeaderBottom();
        grandchildMenu.classList.add(CLASSES.TRANSLATE_X_0);
      });
    });

    const openMenu = () => {
      quickLinksEl?.classList.add('!hidden');
      window.theme?.search?.closeDrawer?.();
      const headerBottom = getHeaderBottom();
      menu.style.top = headerBottom;
      menu.style.maxHeight = `calc(100dvh - ${headerBottom})`;
      menu.style.opacity = '1';
      menu.style.pointerEvents = 'auto';
      if (overlay) {
        overlay.style.top = headerBottom;
        overlay.style.opacity = '1';
        overlay.style.pointerEvents = 'auto';
      }
      toggle.setAttribute(ARIAS.EXPANDED, 'true');
      transparentHeaderUpdate?.();
    };

    const closeMenu = () => {
      menu.style.maxHeight = '0';
      menu.style.opacity = '0';
      menu.style.pointerEvents = 'none';
      if (overlay) {
        overlay.style.opacity = '0';
        overlay.style.pointerEvents = 'none';
      }
      toggle.setAttribute(ARIAS.EXPANDED, 'false');
      transparentHeaderUpdate?.();
      closeAllSubnavs();
      if (isHomepage) quickLinksEl?.classList.remove('!hidden');
    };
    closeHamburgerMenu = closeMenu;

    toggle.addEventListener('click', () => {
      toggle.getAttribute(ARIAS.EXPANDED) === 'true' ? closeMenu() : openMenu();
    });

    if (overlay) overlay.addEventListener('click', closeMenu);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMenu();
    });
  };


  const initDesktopNav = () => {
    const navItems = Array.from(navigationEl.querySelectorAll('[data-nav-desktop-item]'));
    if (!navItems.length) return;

    let activeItem = null;
    const closeTimer = null;

    const closeActive = () => {
      clearTimeout(closeTimer);
      if (!activeItem) return;
      activeItem.classList.remove('nav-open');
      activeItem = null;
      transparentHeaderUpdate?.();
    };

    const openItem = (item) => {
      clearTimeout(closeTimer);
      if (activeItem && activeItem !== item) activeItem.classList.remove('nav-open');
      activeItem = item;
      item.classList.add('nav-open');
      transparentHeaderUpdate?.();
      const submenu = item.querySelector('[data-navigation-menu="2"]');
      if (submenu) submenu.style.top = desktopMediaQuery.matches
        ? `${headerEl.getBoundingClientRect().bottom}px`
        : `${headerEl.offsetHeight}px`;
      if (hamburgerToggle?.getAttribute(ARIAS.EXPANDED) === 'true') {
        closeHamburgerMenu?.();
      }
    };

    const lgMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);

    navItems.forEach(item => {
      const submenu = item.querySelector('[data-navigation-menu="2"]');

      item.addEventListener('mouseenter', () => {
        if (!lgMediaQuery.matches) return;
        if (hamburgerToggle?.getAttribute(ARIAS.EXPANDED) === 'true') {
          closeHamburgerMenu?.();
        }
        openItem(item);
      });

      if (submenu) {
        submenu.addEventListener('mouseleave', () => {
          if (!lgMediaQuery.matches) return;
          closeActive();
        });
      }
    });

    hamburgerToggle?.addEventListener('mouseenter', () => {
      if (!lgMediaQuery.matches) return;
      closeActive();
    });

    const childLinks = navigationEl.querySelectorAll('[data-nav-child-index]');
    childLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        if (!lgMediaQuery.matches) return;
        const idx = link.getAttribute('data-nav-child-index');
        const menu = link.closest(SELECTORS.NAVIGATION_MENU_2);
        if (!menu) return;
        menu.querySelectorAll('[data-nav-grandchild-panel]').forEach(p => p.style.display = 'none');
        const target = menu.querySelector(`[data-nav-grandchild-panel="${idx}"]`);
        if (target) target.style.display = 'flex';
      });
    });
  };

  const positionFixedHeader = () => {
    if (!headerEl.hasAttribute('data-header-fixed')) return;
    const sectionEl = headerEl.parentElement;
    sectionEl.classList.add('fixed');
    if (announcementBarEl) {
      announcementBarEl.classList.add('fixed', 'top-0', 'w-full', 'z-[11]');
      sectionEl.style.top = `${announcementBarEl.offsetHeight}px`;
    } else {
      sectionEl.classList.add('top-0');
    }
    if (!isHomepage) {
      const totalOffset = sectionEl.offsetHeight + (announcementBarEl ? announcementBarEl.offsetHeight : 0);
      mainEl.style.paddingTop = `${totalOffset}px`;
    }
  };

  positionFixedHeader();
  updateMainOffset();
  window.addEventListener('resize', () => {
    positionFixedHeader();
    updateMainOffset();
  }, { passive: true });

  getStickyStatus();
  setStickyScrollListener(desktopMediaQuery);
  desktopMediaQuery.addEventListener('change', () => setStickyScrollListener(desktopMediaQuery));
  initTransparentHeader();
  initHamburgerMenu();
  initDesktopNav();
  initSearch();
};

export default initHeader;
